import { z } from 'zod';
import { PayType, CompensationType } from '~/domain/PermanentJobs/enums';
import { isExcludedProfession } from '../Constants';
import { jobTitleMaxLength, jobDescriptionMinLength, benefitsOtherMaxLength } from '../utils';

/**
 * Helper function to strip HTML tags and get plain text length
 */
const getPlainTextLength = (html: string): number => {
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').trim().length;
};

/**
 * Compensation schema with conditional validation
 * Separated to ensure validation triggers properly
 */
const compensationSchema = z
  .object({
    compensationType: z.enum([CompensationType.FIXED, CompensationType.RANGE]).nullable(),
    minRate: z.string(),
    maxRate: z.string(),
  })
  .superRefine((data, ctx) => {
    // Validate minRate when compensationType is set
    if (data.compensationType && !data.minRate) {
      ctx.addIssue({
        code: 'custom',
        message: 'Minimum pay rate is required',
        path: ['minRate'],
      });
    }

    // Validate maxRate when compensationType is RANGE
    if (data.compensationType === CompensationType.RANGE && !data.maxRate) {
      ctx.addIssue({
        code: 'custom',
        message: 'Maximum pay rate is required for range compensation',
        path: ['maxRate'],
      });
    }

    // Validate maxRate > minRate
    if (data.compensationType === CompensationType.RANGE && data.minRate && data.maxRate) {
      const min = parseFloat(data.minRate.replace(/,/g, ''));
      const max = parseFloat(data.maxRate.replace(/,/g, ''));
      if (!isNaN(min) && !isNaN(max) && max <= min) {
        ctx.addIssue({
          code: 'custom',
          message: 'Maximum pay rate must be greater than minimum pay rate',
          path: ['maxRate'],
        });
      }
    }
  });

// A missing profession and an excluded one are the same problem to the office, so
// they share one message
const professionTypeRequiredMessage = 'Professional type is required';

/**
 * General schema with required fields
 * Validates office, job title, profession type, office specialty, pay type, and schedule type
 */
export const generalSchema = z
  .looseObject({
    // Office selection is required
    office: z.string().min(1, 'Office is required'),

    // Job title with max length validation
    jobTitle: z
      .string()
      .min(1, 'Job title is required')
      .max(jobTitleMaxLength, `Job title must be ${jobTitleMaxLength} characters or less`)
      .refine(
        (value) => value.trim().length > 0,
        'Job title cannot be empty or contain only whitespace',
      ),

    // Profession type is required, and an excluded profession counts as missing:
    // it has no option in the dropdown, so the select renders blank and the office
    // has to pick a supported one before moving on
    professionType: z
      .string()
      .min(1, professionTypeRequiredMessage)
      .refine((value) => !isExcludedProfession(value), professionTypeRequiredMessage),

    // Office specialty is required
    officeSpecialty: z.string().min(1, 'Specialty is required'),

    // Pay type enum
    payType: z.enum([PayType.HOURLY, PayType.SALARY, PayType.DAILY]),

    // Benefits array
    benefits: z.array(z.string()).default([]),

    // Benefits other text (optional with max length)
    benefitsOther: z
      .string()
      .max(
        benefitsOtherMaxLength,
        `Benefits other must be ${benefitsOtherMaxLength} characters or less`,
      )
      .optional(),
  })
  .and(compensationSchema);

/**
 * Schedule schema with required fields
 * Validates schedule type and expected days array (optional)
 */
export const scheduleSchema = z
  .looseObject({
    // Schedule type is required
    scheduleType: z.string().min(1, 'Schedule type is required'),

    // Expected days array (optional)
    expectedDays: z.array(z.number()).default([]),
  })
  .and(compensationSchema);

/**
 * Description schema with required fields
 * Validates job description with min length validation (checks plain text length)
 */
export const descriptionSchema = z.looseObject({
  // Job description with min length validation (checks plain text length)
  jobDescription: z
    .string()
    .min(1, 'Job description is required')
    .refine(
      (html) => getPlainTextLength(html) >= jobDescriptionMinLength,
      `Oops! Job description must be at least ${jobDescriptionMinLength} characters.`,
    ),
});

/**
 * Zod validation schema for Job Details Form
 * Validates all required fields with appropriate rules
 */
export const jobDetailsSchema = generalSchema.and(scheduleSchema).and(descriptionSchema);

export type TJobDetailsSchemas =
  | typeof generalSchema
  | typeof scheduleSchema
  | typeof descriptionSchema
  | typeof jobDetailsSchema;
